import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import {
  FiAlertCircle,
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiDownload,
  FiExternalLink,
  FiFilter,
  FiMic,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiTag,
  FiUser,
  FiFileText,
  FiX
} from 'react-icons/fi';
import { fetchThoughtlyInterviewResponses, isThoughtlyConfigured } from '../../lib/thoughtly';
import { useSettings } from '../../contexts/SettingsContext';

const normalizePhoneNumber = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  return phone.replace(/[^\d+]/g, '').replace(/^(00)/, '+');
};

const parseDurationInSeconds = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    if (value > 0 && value < 1) {
      return Math.round(value * 60);
    }

    return Math.round(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (/^\d+:\d{2}$/.test(trimmed)) {
      const [minutes, seconds] = trimmed.split(':').map(Number);
      return minutes * 60 + seconds;
    }

    const isoMatch = trimmed.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/i);
    if (isoMatch) {
      const hours = parseFloat(isoMatch[1] || '0');
      const minutes = parseFloat(isoMatch[2] || '0');
      const seconds = parseFloat(isoMatch[3] || '0');
      return Math.round(hours * 3600 + minutes * 60 + seconds);
    }

    const numericValue = Number(trimmed);
    if (!Number.isNaN(numericValue)) {
      return Math.round(numericValue);
    }
  }

  return null;
};

const formatDuration = (seconds, fallback) => {
  if (typeof fallback === 'string' && fallback.trim() !== '') {
    return fallback;
  }

  if (seconds == null || Number.isNaN(seconds)) {
    return '—';
  }

  const totalSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const formatDurationFromMilliseconds = (milliseconds) => {
  if (milliseconds == null) {
    return '—';
  }

  const numericValue = Number(milliseconds);

  if (Number.isNaN(numericValue)) {
    return '—';
  }

  const totalSeconds = Math.max(0, Math.round(numericValue / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  }

  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
  }

  return parts.join(' ');
};

const extractCost = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    const inferredAmount = value > 10 ? value : value / 100;
    return {
      amount: inferredAmount,
      currency: 'USD'
    };
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return {
        amount: parsed,
        currency: 'USD'
      };
    }
  }

  if (typeof value === 'object') {
    if (value.amount_cents || value.cents) {
      const cents = value.amount_cents ?? value.cents;
      return {
        amount: Number(cents) / 100,
        currency: value.currency || value.currency_code || 'USD'
      };
    }

    const amount =
      value.total ??
      value.amount ??
      value.value ??
      value.price ??
      value.subtotal ??
      (value.data && (value.data.total || value.data.amount));

    if (amount != null) {
      const parsedAmount = typeof amount === 'string' ? Number(amount) : amount;
      if (!Number.isNaN(parsedAmount)) {
        return {
          amount: parsedAmount,
          currency: value.currency || value.currency_code || 'USD'
        };
      }
    }
  }

  return null;
};

const formatCurrency = (cost) => {
  if (!cost) {
    return null;
  }

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cost.currency || 'USD'
    }).format(cost.amount);
  } catch (error) {
    console.warn('Failed to format currency value from Thoughtly response', error);
    return `$${cost.amount.toFixed(2)}`;
  }
};

const extractTextValue = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    const combined = value
      .map((item) => extractTextValue(item))
      .filter(Boolean)
      .join('\n')
      .trim();

    return combined.length > 0 ? combined : null;
  }

  if (typeof value === 'object') {
    const keysToCheck = ['text', 'summary', 'content', 'notes', 'message', 'value', 'result', 'description', 'details'];

    for (const key of keysToCheck) {
      if (key in value) {
        const extracted = extractTextValue(value[key]);
        if (extracted) {
          return extracted;
        }
      }
    }
  }

  return null;
};

const coalesceText = (...values) => {
  for (const value of values) {
    const text = extractTextValue(value);
    if (text) {
      return text;
    }
  }

  return null;
};

const buildConversationTranscript = (conversationHistory) => {
  if (!Array.isArray(conversationHistory)) {
    return [];
  }

  return conversationHistory
    .map((entry) => {
      if (!entry) {
        return null;
      }

      const authorRaw = (entry.author || entry.role || '').toString().toLowerCase();
      if (authorRaw !== 'ai' && authorRaw !== 'user') {
        return null;
      }

      const authorLabel = authorRaw === 'ai' ? 'AI' : 'User';
      const text =
        extractTextValue(entry.text) ||
        extractTextValue(entry.content) ||
        extractTextValue(entry.message) ||
        extractTextValue(entry.body) ||
        extractTextValue(entry.value) ||
        null;

      if (!text) {
        return null;
      }

      return { author: authorLabel, text };
    })
    .filter(Boolean);
};

const buildMetadataEntries = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return [];
  }

  return Object.entries(metadata)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      const label = key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

      let displayValue = value;
      if (typeof value === 'object') {
        try {
          displayValue = JSON.stringify(value, null, 2);
        } catch (error) {
          displayValue = String(value);
        }
      }

      return { key, label, value: displayValue };
    });
};

const normaliseThoughtlyResponse = (response, agent, targetPhone, targetEmail) => {
  if (!response) {
    return null;
  }

  const agentId = agent?.thoughtlyAgentId || agent?.id;
  const baseId =
    response.response_id ||
    response.responseId ||
    response.id ||
    response.uuid ||
    response.call_id ||
    response.callId ||
    `${agentId || 'agent'}-${response.created_at || response.updated_at || Date.now()}`;

  const createdAt =
    response.created_at ||
    response.createdAt ||
    response.timestamp ||
    response.completed_at ||
    response.completedAt ||
    response.started_at ||
    response.startedAt ||
    null;

  const durationSeconds =
    parseDurationInSeconds(response.duration_seconds) ||
    parseDurationInSeconds(response.duration) ||
    parseDurationInSeconds(response.call_duration) ||
    parseDurationInSeconds(response.metadata?.duration) ||
    null;

  const durationDisplay =
    response.duration_display ||
    response.durationFormatted ||
    response.metadata?.duration_formatted ||
    response.metadata?.duration_display ||
    null;

  const summary = coalesceText(
    response.summary?.text,
    response.summary_text,
    response.ai_summary,
    response.summary,
    response.metadata?.summary,
    response.metadata?.ai_summary,
    response.outcome,
    response.summary?.outcome,
    response.metadata?.outcome,
    response.metadata?.call_outcome,
    response.call_outcome,
    response.disposition,
    response.metadata?.disposition,
    response.notes,
    response.metadata?.notes,
    response.metadata?.call_notes,
    response.metadata?.conversation_summary,
    response.result,
    response.metadata?.result
  );

  const outcome =
    response.outcome ||
    response.summary?.outcome ||
    response.metadata?.outcome ||
    response.metadata?.call_outcome ||
    response.call_outcome ||
    null;

  const statusValue =
    response.status ||
    response.call_status ||
    response.result ||
    response.state ||
    response.metadata?.status;

  const status = typeof statusValue === 'string'
    ? statusValue.toLowerCase().replace(/\s+/g, '_')
    : statusValue || 'unknown';

  const agentName =
    agent?.name ||
    response.agent?.name ||
    response.interview?.title ||
    response.agent_name ||
    response.metadata?.agent_name ||
    'Thoughtly AI Agent';

  const contactName =
    response.contact?.name ||
    response.metadata?.contact_name ||
    response.metadata?.name ||
    null;

  const phoneCandidates = [
    response.phone_number,
    response.phoneNumber,
    response.contact?.phone,
    response.contact?.phone_number,
    response.metadata?.phone,
    response.metadata?.phone_number,
    response.metadata?.dialed_phone_number,
    response.metadata?.contact_phone,
    response.metadata?.prospect_phone,
    response.call?.phone_number
  ]
    .concat(Array.isArray(response.phone_numbers) ? response.phone_numbers : [])
    .filter(Boolean);

  const emailCandidates = [
    response.contact?.email,
    response.metadata?.contact_email,
    response.metadata?.email,
    response.metadata?.prospect_email,
    response.email
  ]
    .concat(Array.isArray(response.emails) ? response.emails : [])
    .filter(Boolean)
    .map((value) => value.toLowerCase());

  const contactIdentifiers = [
    response.contact?.id,
    response.contact?.identifier,
    response.metadata?.contact_id,
    response.metadata?.contact_identifier,
    response.metadata?.prospect_id
  ].filter(Boolean);

  const normalisedPhones = phoneCandidates
    .map((value) => normalizePhoneNumber(String(value)))
    .filter(Boolean);

  const matchesPhone =
    targetPhone && normalisedPhones.some((value) => value.endsWith(targetPhone));

  const matchesEmail =
    targetEmail && emailCandidates.some((value) => value === targetEmail);

  if (targetPhone || targetEmail) {
    if (!matchesPhone && !matchesEmail) {
      return null;
    }
  }

  const cost =
    extractCost(response.cost) ||
    extractCost(response.billing) ||
    extractCost(response.pricing) ||
    null;

  const transcriptSource =
    response.transcript?.text ||
    response.transcript_text ||
    response.transcript ||
    response.metadata?.transcript ||
    null;

  const transcript = Array.isArray(transcriptSource)
    ? transcriptSource
        .map((segment) => {
          if (!segment) {
            return null;
          }

          if (typeof segment === 'string') {
            return segment;
          }

          if (segment.text) {
            return `${segment.speaker ? `${segment.speaker}: ` : ''}${segment.text}`;
          }

          return JSON.stringify(segment);
        })
        .filter(Boolean)
        .join('\\n')
    : transcriptSource;

  const tags = Array.isArray(response.tags)
    ? response.tags
    : Array.isArray(response.metadata?.tags)
      ? response.metadata.tags
      : Array.isArray(response.metadata?.tag_list)
        ? response.metadata.tag_list
        : [];

  const responses = Array.isArray(response.responses)
    ? response.responses
    : Array.isArray(response.data?.responses)
      ? response.data.responses
      : [];

  return {
    id: baseId,
    responseId: response.response_id || response.responseId || response.id || null,
    interviewId: response.interview_id || response.interviewId || agentId || null,
    agentId,
    agentName,
    contactName,
    contactIdentifiers,
    contactPhonesNormalized: normalisedPhones,
    contactEmails: emailCandidates,
    createdAt,
    updatedAt: response.updated_at || response.updatedAt || null,
    durationSeconds,
    durationDisplay,
    status,
    outcome,
    summary,
    transcript,
    transcriptUrl:
      response.transcript_url ||
      response.transcriptUrl ||
      response.metadata?.transcript_url ||
      null,
    recordingUrl:
      response.recording_url ||
      response.recordingUrl ||
      response.metadata?.recording_url ||
      null,
    metadata: response.metadata || response.meta || {},
    tags,
    responses,
    cost,
    costFormatted: formatCurrency(cost),
    raw: response
  };
};

const statusStyles = {
  completed: 'bg-green-100 text-green-800 border-green-200',
  success: 'bg-green-100 text-green-800 border-green-200',
  succeeded: 'bg-green-100 text-green-800 border-green-200',
  interested: 'bg-green-100 text-green-800 border-green-200',
  meeting_confirmed: 'bg-green-100 text-green-800 border-green-200',
  scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  connecting: 'bg-blue-100 text-blue-800 border-blue-200',
  queued: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  retry: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  no_answer: 'bg-orange-100 text-orange-800 border-orange-200',
  voicemail: 'bg-orange-100 text-orange-800 border-orange-200',
  busy: 'bg-orange-100 text-orange-800 border-orange-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  error: 'bg-red-100 text-red-800 border-red-200',
  canceled: 'bg-gray-100 text-gray-700 border-gray-200',
  cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
  unknown: 'bg-gray-100 text-gray-700 border-gray-200'
};

const AICallSummaryTab = ({
  contact,
  agents = [],
  onMakeAICall,
  isActive,
  onCallCountChange
}) => {
  const { settings } = useSettings();
  const thoughtlyApiKey = settings?.thoughtlyApiKey;
  const thoughtlyTeamId = settings?.thoughtlyTeamId;

  const configOverrides = useMemo(() => {
    const overrides = {};
    if (thoughtlyApiKey) {
      overrides.apiKey = thoughtlyApiKey;
    }
    if (thoughtlyTeamId) {
      overrides.teamId = thoughtlyTeamId;
    }
    return overrides;
  }, [thoughtlyApiKey, thoughtlyTeamId]);

  const hasThoughtlyConfig = useMemo(() => {
    try {
      return isThoughtlyConfigured(configOverrides);
    } catch (error) {
      console.error('Failed to evaluate Thoughtly configuration', error);
      return false;
    }
  }, [configOverrides]);

  const agentLookup = useMemo(() => {
    const map = new Map();
    agents.forEach((agent) => {
      const identifiers = [agent.thoughtlyAgentId, agent.id].filter(Boolean);
      identifiers.forEach((identifier) => {
        if (!map.has(identifier)) {
          map.set(identifier, agent);
        }
      });
    });
    return map;
  }, [agents]);

  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCall, setExpandedCall] = useState(null);
  const [activeTranscriptCall, setActiveTranscriptCall] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const targetPhone = useMemo(() => normalizePhoneNumber(contact?.phone || ''), [contact?.phone]);
  const targetEmail = useMemo(() => (contact?.email ? contact.email.toLowerCase() : ''), [contact?.email]);

  const agentIdentifiers = useMemo(
    () => Array.from(new Set(agents.map((agent) => agent.thoughtlyAgentId || agent.id).filter(Boolean))),
    [agents]
  );

  const loadCallHistory = useCallback(async () => {
    if (!isActive) {
      return;
    }

    if (!contact?.phone && !contact?.email) {
      setError('This contact does not have a phone number or email to match call history.');
      setCalls([]);
      setHasLoaded(true);
      onCallCountChange?.(0);
      return;
    }

    if (!hasThoughtlyConfig) {
      setError('Thoughtly API credentials are not configured.');
      setCalls([]);
      setHasLoaded(true);
      onCallCountChange?.(0);
      return;
    }

    if (agentIdentifiers.length === 0) {
      setError('No Thoughtly AI agents are available for this workspace.');
      setCalls([]);
      setHasLoaded(true);
      onCallCountChange?.(0);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await Promise.allSettled(
        agentIdentifiers.map(async (agentId) => {
          const responses = await fetchThoughtlyInterviewResponses(agentId, configOverrides);
          return { agentId, responses };
        })
      );

      const aggregated = new Map();
      const partialErrors = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { agentId, responses } = result.value;
          const agent = agentLookup.get(agentId) || agentLookup.get(String(agentId));

          if (Array.isArray(responses)) {
            responses.forEach((response) => {
              const normalised = normaliseThoughtlyResponse(response, agent, targetPhone, targetEmail);
              if (!normalised) {
                return;
              }

              if (!normalised.id) {
                return;
              }

              aggregated.set(normalised.id, normalised);
            });
          }
        } else {
          partialErrors.push(result.reason);
        }
      });

      const sortedCalls = Array.from(aggregated.values()).sort((a, b) => {
        const first = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const second = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return second - first;
      });

      setCalls(sortedCalls);
      onCallCountChange?.(sortedCalls.length);
      setLastUpdated(new Date());

      if (partialErrors.length > 0) {
        console.warn('Some Thoughtly call responses could not be loaded', partialErrors);
        setError('Some Thoughtly call records could not be loaded. Try refreshing.');
      } else {
        setError(null);
      }
    } catch (loadError) {
      console.error('Failed to load Thoughtly call history', loadError);
      setCalls([]);
      onCallCountChange?.(0);
      setError(loadError.message || 'Unable to load Thoughtly call history.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
      setHasLoaded(true);
    }
  }, [
    agentIdentifiers,
    agentLookup,
    configOverrides,
    contact?.email,
    contact?.phone,
    hasThoughtlyConfig,
    isActive,
    onCallCountChange,
    targetEmail,
    targetPhone
  ]);

  useEffect(() => {
    setCalls([]);
    setExpandedCall(null);
    setHasLoaded(false);
    setError(null);
    setLastUpdated(null);
  }, [contact?.id]);

  useEffect(() => {
    if (isActive && !hasLoaded) {
      loadCallHistory();
    }
  }, [isActive, hasLoaded, loadCallHistory]);

  useEffect(() => {
    if (!activeTranscriptCall) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveTranscriptCall(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeTranscriptCall]);

  const filteredCalls = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return calls.filter((call) => {
      const callRecord = call.raw || call;
      const summaryFromRecord = extractTextValue(callRecord?.summary_data?.summary);
      const tagList = Array.isArray(callRecord?.tags) ? callRecord.tags : call.tags;
      const matchesSearch =
        search === '' ||
        (call.agentName && call.agentName.toLowerCase().includes(search)) ||
        (summaryFromRecord && summaryFromRecord.toLowerCase().includes(search)) ||
        (call.summary && call.summary.toLowerCase().includes(search)) ||
        (call.outcome && call.outcome.toLowerCase().includes(search)) ||
        (Array.isArray(tagList) && tagList.some((tag) => String(tag).toLowerCase().includes(search)));

      const matchesStatus = statusFilter === 'all' || call.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [calls, searchTerm, statusFilter]);

  const statusOptions = useMemo(() => {
    const unique = Array.from(new Set(calls.map((call) => call.status).filter(Boolean)));
    return unique;
  }, [calls]);

  const toggleExpanded = (callId) => {
    setExpandedCall((current) => (current === callId ? null : callId));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadCallHistory();
  };

  const handleMakeCall = () => {
    if (onMakeAICall) {
      onMakeAICall();
    }
  };

  const renderCallCard = (call, index) => {
    const isExpanded = expandedCall === call.id;
    const statusClass = statusStyles[call.status] || statusStyles.unknown;
    const createdAt = call.createdAt ? new Date(call.createdAt) : null;
    const formattedDate = createdAt ? format(createdAt, "MMM d, yyyy 'at' h:mm a") : 'Unknown time';
    const relativeTime = createdAt ? formatDistanceToNow(createdAt, { addSuffix: true }) : null;
    const duration = formatDuration(call.durationSeconds, call.durationDisplay);
    const metadataEntries = buildMetadataEntries(call.metadata);
    const callRecord = call.raw || call;
    const summaryTextRaw = callRecord?.summary_data?.summary;
    const summaryText = extractTextValue(summaryTextRaw);
    const truncatedSummary = summaryText
      ? summaryText.length > 320
        ? `${summaryText.slice(0, 317)}…`
        : summaryText
      : null;
    const transcriptEntries = buildConversationTranscript(callRecord?.conversation_history);
    const hasTranscriptEntries = transcriptEntries.length > 0;
    const transcriptUrl = call.transcriptUrl || callRecord?.transcript_url || null;
    const hasMetadata = metadataEntries.length > 0;
    const disposition =
      Array.isArray(callRecord?.dispositions) && callRecord.dispositions.length > 0
        ? callRecord.dispositions[0]
        : null;
    const durationFromMilliseconds = formatDurationFromMilliseconds(callRecord?.duration_ms);
    const durationLabel = durationFromMilliseconds !== '—' ? durationFromMilliseconds : duration;
    const tags = Array.isArray(callRecord?.tags) && callRecord.tags.length > 0 ? callRecord.tags : call.tags || [];

    return (
      <div key={call.id} className="relative pl-10">
        <div className="absolute left-[0.675rem] top-4 h-full w-px bg-gradient-to-b from-purple-200 via-blue-100 to-transparent" />
        <div className="absolute left-2 top-6 h-3 w-3 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 shadow-md" />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="mb-6 rounded-xl border border-purple-100 bg-white shadow-sm transition-shadow hover:shadow-md"
        >
          <button
            type="button"
            onClick={() => toggleExpanded(call.id)}
            className="flex w-full flex-col gap-4 rounded-xl p-5 text-left"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-inner">
                  <SafeIcon icon={FiMic} />
                </div>
                <div>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <SafeIcon icon={FiClock} />
                    <span>{formattedDate}</span>
                    {relativeTime && <span className="text-gray-400">({relativeTime})</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {call.agentName}
                    </h4>
                    {disposition?.name && (
                      <span
                        className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
                        title={disposition.description || ''}
                      >
                        {String(disposition.name).replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <SafeIcon icon={FiUser} />
                      {call.contactName || `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || 'Contact'}
                    </span>
                    {contact?.phone && (
                      <span className="flex items-center gap-1">
                        <SafeIcon icon={FiPhone} />
                        {contact.phone}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <SafeIcon icon={FiClock} />
                      {durationLabel}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-3">
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusClass}`}>
                  {call.status.replace(/_/g, ' ')}
                </span>
                {call.costFormatted && (
                  <span className="text-sm font-semibold text-purple-600">
                    {call.costFormatted}
                  </span>
                )}
                <SafeIcon icon={isExpanded ? FiChevronDown : FiChevronRight} className="text-gray-500" />
              </div>
            </div>
            {isExpanded && summaryText && (
              <p className="text-sm leading-6 text-gray-700">{summaryText}</p>
            )}
            {!isExpanded && truncatedSummary && (
              <p className="text-sm leading-6 text-gray-700">{truncatedSummary}</p>
            )}
            {Array.isArray(tags) && tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700"
                  >
                    <SafeIcon icon={FiTag} className="text-[10px]" />
                    {String(tag).replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}
          </button>
          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="border-t border-purple-100 bg-gradient-to-br from-purple-50/60 via-blue-50/40 to-white p-5"
              >
                <div className="space-y-6">
                  {(hasTranscriptEntries || transcriptUrl) && (
                    <div className="flex flex-wrap gap-3">
                      {hasTranscriptEntries && (
                        <button
                          type="button"
                          onClick={() =>
                            setActiveTranscriptCall({
                              id: call.id,
                              agentName: call.agentName,
                              transcriptEntries,
                              transcriptUrl
                            })
                          }
                          className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-sm font-medium text-purple-700 transition-colors hover:border-purple-300 hover:text-purple-800"
                        >
                          <SafeIcon icon={FiFileText} className="text-xs" />
                          View Transcript
                        </button>
                      )}
                      {transcriptUrl && (
                        <a
                          href={transcriptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-sm font-medium text-purple-700 transition-colors hover:border-purple-300 hover:text-purple-800"
                        >
                          <SafeIcon icon={FiExternalLink} className="text-xs" />
                          Open transcript
                        </a>
                      )}
                    </div>
                  )}
                  <div className="space-y-3">
                    <h5 className="text-sm font-semibold text-gray-900">Recording</h5>
                    {call.recordingUrl ? (
                      <div className="space-y-3">
                        <audio controls className="w-full">
                          <source src={call.recordingUrl} />
                          Your browser does not support the audio element.
                        </audio>
                        <div className="flex flex-wrap gap-3 text-sm">
                          <a
                            href={call.recordingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-purple-700 transition-colors hover:border-purple-300 hover:text-purple-800"
                          >
                            <SafeIcon icon={FiExternalLink} className="text-xs" />
                            Open recording
                          </a>
                          <a
                            href={call.recordingUrl}
                            download
                            className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-purple-700 transition-colors hover:border-purple-300 hover:text-purple-800"
                          >
                            <SafeIcon icon={FiDownload} className="text-xs" />
                            Download
                          </a>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No recording available.</p>
                    )}
                  </div>
                  {hasMetadata && (
                    <div className="space-y-3">
                      <h5 className="text-sm font-semibold text-gray-900">Metadata</h5>
                      <dl className="grid gap-3 sm:grid-cols-2">
                        {metadataEntries.map((entry) => (
                          <div key={entry.key} className="rounded-lg border border-purple-100 bg-white/70 p-3">
                            <dt className="text-xs font-semibold uppercase tracking-wide text-purple-700">{entry.label}</dt>
                            <dd className="mt-1 whitespace-pre-wrap text-sm leading-5 text-gray-800">{entry.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </div>

                {call.responses && call.responses.length > 0 && (
                  <div className="mt-5 space-y-2">
                    <h5 className="text-sm font-semibold text-gray-900">Response Data</h5>
                    <div className="space-y-2">
                      {call.responses.map((item, idx) => (
                        <div key={idx} className="rounded-lg border border-purple-100 bg-white/70 p-3 text-sm text-gray-700">
                          <pre className="whitespace-pre-wrap text-[13px] leading-5 text-gray-800">
                            {typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">AI Caller Summary</h3>
          <p className="text-sm text-gray-600">
            Review Thoughtly AI calls placed to {contact?.firstName || contact?.lastName ? `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() : 'this contact'}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleMakeCall}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-lg"
          >
            <SafeIcon icon={FiMic} className="text-xs" />
            Quick AI Call
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isLoading}
            className={`inline-flex items-center gap-2 rounded-lg border border-purple-200 px-4 py-2 text-sm font-medium text-purple-700 transition-colors ${
              isLoading
                ? 'cursor-not-allowed opacity-60'
                : 'hover:border-purple-300 hover:text-purple-800'
            }`}
          >
            <SafeIcon
              icon={FiRefreshCw}
              className={`${isRefreshing || isLoading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {lastUpdated && (
        <p className="text-xs uppercase tracking-wide text-gray-400">
          Last updated {formatDistanceToNow(lastUpdated, { addSuffix: true })}
        </p>
      )}

      {!hasThoughtlyConfig && (
        <div className="flex items-start gap-3 rounded-xl border border-dashed border-purple-300 bg-purple-50/60 p-4 text-sm text-purple-700">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5 text-base" />
          <div>
            <p className="font-semibold">Connect your Thoughtly account to view AI call history.</p>
            <p className="text-purple-600/80">
              Add your Thoughtly API key and team ID in Settings &rarr; Integrations to sync call data automatically.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50/80 p-4 text-sm text-red-700">
          <div className="flex items-start gap-2">
            <SafeIcon icon={FiAlertCircle} className="mt-0.5 text-base" />
            <div>
              <p className="font-semibold">Unable to load Thoughtly call history</p>
              <p>{error}</p>
            </div>
          </div>
          <div>
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:border-red-300 hover:text-red-800"
            >
              <SafeIcon icon={FiRefreshCw} className="text-xs" />
              Retry
            </button>
          </div>
        </div>
      )}

      {isLoading && calls.length === 0 && (
        <div className="space-y-4 rounded-xl border border-purple-100 bg-white p-6">
          <div className="h-4 w-48 animate-pulse rounded-full bg-purple-100" />
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="space-y-3">
                <div className="h-4 w-full animate-pulse rounded-full bg-purple-50" />
                <div className="h-3 w-2/3 animate-pulse rounded-full bg-purple-50" />
              </div>
            ))}
          </div>
        </div>
      )}

      {calls.length > 0 && (
        <div className="flex flex-col gap-4 rounded-xl border border-purple-100 bg-white/70 p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                placeholder="Search calls, outcomes, or tags"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full rounded-lg border border-purple-100 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 shadow-inner focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
            </div>
            <div>
              <label className="sr-only" htmlFor="ai-call-status-filter">
                Filter by status
              </label>
              <div className="relative">
                <FiFilter className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select
                  id="ai-call-status-filter"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="min-w-[160px] appearance-none rounded-lg border border-purple-100 bg-white py-2 pl-9 pr-8 text-sm text-gray-700 focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-200"
                >
                  <option value="all">All statuses</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="relative mt-2">
            <div className="absolute left-5 top-0 bottom-0 w-px bg-gradient-to-b from-purple-200 via-blue-100 to-transparent" />
            <div className="space-y-4">
              {filteredCalls.length > 0 ? (
                filteredCalls.map((call, index) => renderCallCard(call, index))
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-purple-200 bg-purple-50/70 p-6 text-center text-sm text-purple-700">
                  <SafeIcon icon={FiSearch} className="text-xl" />
                  <p>No calls match your current filters.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                    }}
                    className="text-xs font-semibold uppercase tracking-wide text-purple-600 hover:text-purple-800"
                  >
                    Reset filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!isLoading && calls.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-purple-200 bg-purple-50/70 p-10 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg">
            <SafeIcon icon={FiMic} className="text-2xl" />
          </div>
          <div className="space-y-2">
            <h4 className="text-lg font-semibold text-gray-900">No AI calls have been made to this contact yet</h4>
            <p className="text-sm text-gray-600">
              Kickstart the conversation by launching a Thoughtly-powered AI call.
            </p>
          </div>
          <button
            type="button"
            onClick={handleMakeCall}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-lg"
          >
            <SafeIcon icon={FiMic} className="text-xs" />
          Quick AI Call
        </button>
      </div>
      )}

      {activeTranscriptCall && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-4"
          onClick={() => setActiveTranscriptCall(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <SafeIcon icon={FiFileText} />
                Full Transcript
              </div>
              <button
                type="button"
                onClick={() => setActiveTranscriptCall(null)}
                className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close transcript"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
              {activeTranscriptCall.transcriptEntries && activeTranscriptCall.transcriptEntries.length > 0 ? (
                <div className="space-y-3">
                  {activeTranscriptCall.transcriptEntries.map((entry, index) => (
                    <div key={`${activeTranscriptCall.id}-${index}`} className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">{entry.author}</p>
                      <p className="mt-1 text-sm leading-6 text-gray-800 whitespace-pre-wrap">{entry.text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No transcript available for this call.</p>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
              <div className="text-sm text-gray-500">
                {activeTranscriptCall.agentName && (
                  <span className="font-medium text-gray-700">{activeTranscriptCall.agentName}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                {activeTranscriptCall.transcriptUrl && (
                  <a
                    href={activeTranscriptCall.transcriptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-sm font-medium text-purple-700 transition-colors hover:border-purple-300 hover:text-purple-800"
                  >
                    <SafeIcon icon={FiExternalLink} className="text-xs" />
                    Open transcript
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setActiveTranscriptCall(null)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AICallSummaryTab;
